import React, { useMemo, useEffect, useState } from 'react';
import {
    createColumnHelper,
    getCoreRowModel,
    getPaginationRowModel,
    getFilteredRowModel,
    functionalUpdate,
    SortingState,
    ColumnFiltersState,
    useReactTable
} from '@tanstack/react-table';
import { CollaboratorPublic } from '@/features/Collaborators/Data';
import NameCell from '@/features/Collaborators/List/Tables/NameCell';
import { TanStackTableView } from '@/components/shared';
import { ActionMenu, ActionMenuItem } from '@/components/shared';
import {
    PencilIcon,
    StarIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { compareStringsAccentInsensitive, normalizeString } from '@/utils/stringUtils';
import { useAuth, ROLE_ADMIN, ROLE_COLLABORATOR_ADMIN, ROLE_COLLABORATOR_EDITOR } from '@/contexts/AuthContext';

interface CollaboratorDirectoryTableViewProps {
    collaborators: CollaboratorPublic[];
    isLoading: boolean;
    onEditClick?: (collaboratorId: string) => void;
    nameSearchQuery: string;
    showActions?: boolean;
}

const columnHelper = createColumnHelper<CollaboratorPublic>();

const CollaboratorDirectoryTableView: React.FC<CollaboratorDirectoryTableViewProps> = ({
    collaborators,
    isLoading,
    onEditClick,
    nameSearchQuery,
    showActions = false
}) => {
    const [sorting, setSorting] = useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const { userHasRole } = useAuth();

    // Verificar se usuário pode editar (admin, collaborator_admin ou collaborator_editor)
    const canEdit = userHasRole(ROLE_ADMIN) || userHasRole(ROLE_COLLABORATOR_ADMIN) || userHasRole(ROLE_COLLABORATOR_EDITOR);

    const columns = useMemo(() => [
        columnHelper.accessor('name', {
            header: 'NOME',
            cell: ({ row }) => (
                <NameCell
                    name={row.original.name}
                    subtitle={row.original.positionName}
                    avatarUrl={row.original.photoUrl}
                    linkTo={`/collaborators/${row.original.id}`}
                />
            ),
            enableSorting: true,
            sortingFn: (rowA, rowB, columnId) =>
                compareStringsAccentInsensitive(
                    rowA.getValue(columnId) as string,
                    rowB.getValue(columnId) as string
                ),
            filterFn: (row, columnId, filterValue) => {
                const value = normalizeString(row.getValue<string>(columnId));
                return value.includes(normalizeString(filterValue));
            },
        }),
        columnHelper.accessor('departmentName', {
            header: 'DEPARTAMENTO',
            cell: ({ row }) => {
                const city = row.original.city;
                const state = row.original.state;
                let location = '';

                if (city && state) {
                    location = `${city}, ${state}`;
                } else if (city || state) {
                    location = city || state || '';
                }

                return (
                    <div className="text-sm">
                        <div className="font-medium text-gray-900">
                            {row.original.departmentName || '-'}
                        </div>
                        <div className="text-gray-500">
                            {location || '-'}
                        </div>
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: (rowA, rowB, columnId) =>
                compareStringsAccentInsensitive(
                    (rowA.getValue(columnId) as string) || '',
                    (rowB.getValue(columnId) as string) || ''
                ),
        }),
        columnHelper.display({
            id: 'contacts',
            header: 'CONTATOS',
            size: 200,
            maxSize: 250,
            cell: ({ row }) => {
                const email = row.original.email;
                const phone = row.original.phone;

                return (
                    <div className="text-sm">
                        <div className="mb-1">
                            {email ? (
                                <a
                                    href={`mailto:${email}`}
                                    className="text-blue-600 hover:text-blue-800 font-medium"
                                    title={`Enviar email para ${email}`}
                                >
                                    {email}
                                </a>
                            ) : (
                                <span className="text-gray-500">-</span>
                            )}
                        </div>
                        <div>
                            {phone ? (
                                <a
                                    href={`https://wa.me/${phone.replace(/\D/g, '')}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-green-600 hover:text-green-800 font-medium"
                                    title={`Enviar WhatsApp para ${phone}`}
                                >
                                    {phone}
                                </a>
                            ) : (
                                <span className="text-gray-500">-</span>
                            )}
                        </div>
                    </div>
                );
            },
        }),
        columnHelper.display({
            id: 'skills',
            header: 'SKILLS',
            size: 300,
            minSize: 250,
            cell: ({ row }) => {
                const resources = row.original.resources || [];

                if (resources.length === 0) {
                    return <div className="text-sm text-gray-500">-</div>;
                }

                // Separar recurso principal dos secundários
                const mainResource = resources.find((r: any) => r.isPrimary || r.isMain) || resources[0];
                const secondaryResources = resources.filter((r: any, index: number) =>
                    (r.isPrimary || r.isMain) ? false : index !== 0
                );

                const getResourceDisplay = (resource: any) => {
                    // Tentar diferentes formas de obter o nome da tecnologia/tipo
                    const techName = resource.technologyName || resource.technology?.name || resource.tech;
                    const typeName = resource.typeName || resource.type?.name || resource.resourceType;

                    // Priorizar tecnologia, depois tipo
                    const skillName = techName || typeName || 'Skill';

                    return { skillName };
                };

                return (
                    <div className="text-sm space-y-1">
                        {/* Skill Principal */}
                        {mainResource && (
                            <div className="flex items-center gap-1">
                                <StarIconSolid className="h-3 w-3 text-amber-500 flex-shrink-0" title="Skill Principal" />
                                <span className="text-xs font-semibold text-gray-900">
                                    {getResourceDisplay(mainResource).skillName}
                                </span>
                            </div>
                        )}

                        {/* Skills Secundários */}
                        {secondaryResources.length > 0 && (
                            <div className="space-y-1 ml-4">
                                {secondaryResources.slice(0, 2).map((resource: any, index: number) => {
                                    const { skillName } = getResourceDisplay(resource);
                                    return (
                                        <div key={index}>
                                            <span className="text-xs text-gray-700">
                                                {skillName}
                                            </span>
                                        </div>
                                    );
                                })}
                                {secondaryResources.length > 2 && (
                                    <span className="text-xs text-gray-400 italic">
                                        +{secondaryResources.length - 2} mais
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                );
            },
        }),
        // Coluna de ações - só aparece se usuário for admin E showActions for true
        ...(canEdit && showActions && onEditClick ? [columnHelper.display({
            id: 'actions',
            header: 'AÇÕES',
            cell: ({ row }) => {
                const collaboratorId = row.original.id;

                const actionItems: ActionMenuItem[] = [
                    {
                        label: 'Editar',
                        onClick: () => onEditClick(collaboratorId),
                        icon: PencilIcon,
                    },
                ];

                return (
                    <div className="flex justify-end">
                        <ActionMenu items={actionItems} />
                    </div>
                );
            },
            size: 80,
            maxSize: 80,
        })] : []),
    ], [onEditClick, canEdit]);

    const table = useReactTable({
        data: collaborators,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        state: { sorting, columnFilters },
        onSortingChange: updater => setSorting(prev => functionalUpdate(updater, prev)),
        onColumnFiltersChange: updater => setColumnFilters(prev => functionalUpdate(updater, prev)),
    });

    // Sincroniza nameSearchQuery -> columnFilter
    useEffect(() => {
        table.getColumn('name')?.setFilterValue(nameSearchQuery);
    }, [nameSearchQuery, table]);

    return (
        <TanStackTableView
            table={table}
            isLoading={isLoading}
            loadingMessage="Carregando colaboradores..."
        />
    );
};

export default CollaboratorDirectoryTableView; 
